import { calculateOverdueDays } from '@/utils/common'
import dayjs from 'dayjs'

export const getAssetInfo = (loan, assetClassification) => {
  if (!assetClassification || !Array.isArray(assetClassification)) return null

  // Calculate DPD
  const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
  const overdueEmis = schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))

  let currentDpd = 0
  if (overdueEmis.length > 0) {
    const oldestOverdue = overdueEmis.sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0]
    currentDpd = dayjs().diff(dayjs(oldestOverdue.dueDate), 'day')
  }

  // Calculate Principal Outstanding (POS)
  let pos = 0
  if (schedule.length > 0) {
    pos = schedule
      .filter((e) => !e.isPaid)
      .reduce((sum, e) => sum + (Number(e.principalAmount) || 0), 0)
  } else {
    pos = Number(loan?.loanDetails?.outstandingPrincipal || loan?.outstandingAmount || 0)
  }

  // Find matching classification bucket
  const classification = assetClassification.find((c) => {
    const minDpd = Number(c.minDpd)
    const maxDpd = Number(c.maxDpd)
    const hasMax = c.maxDpd !== undefined && c.maxDpd !== null && c.maxDpd !== ''
    return currentDpd >= minDpd && (!hasMax || currentDpd <= maxDpd)
  })

  if (classification) {
    const percentage = Number(classification.provisioningPercentage) || 0
    return {
      category: classification.category,
      provisioningPercentage: percentage,
      provisioningAmount: (pos * percentage) / 100,
      principalOutstanding: pos,
      dpd: currentDpd
    }
  }
  return null
}

export const COLUMN_MAPPERS = {
  asset_category: ({ loan }) => loan?.assetInfo?.category || '-',
  provisioning_amount: ({ loan }) => (loan?.assetInfo?.provisioningAmount || 0).toFixed(2),
  loan_account_no: ({ loan }) =>
    loan?.applicationNumber || loan?.caseNo || loan?.loanDetails?.applicationNumber || '-',
  customer_name: ({ loan, emi }) =>
    emi?.customer?.customerFullName || loan?.customer?.customerFullName || '-',
  amount_collected: ({ emi }) => (emi?.emiAmount || 0).toFixed(2),
  collection_date: ({ emi }) =>
    emi?.paymentDate ? dayjs(emi.paymentDate).format('DD/MM/YYYY') : '-',
  agent_name: ({ loan, rowData }) => {
    if (rowData?.agent_name) return rowData.agent_name
    if (loan?.assignedTo && Array.isArray(loan.assignedTo) && loan.assignedTo.length > 0) {
      if (typeof loan.assignedTo[0] === 'object') {
        return loan.assignedTo.map((u) => `${u.firstName} ${u.lastName || ''}`).join(', ')
      }
    }
    return loan?.createdBy ? `${loan.createdBy.firstName} ${loan.createdBy.lastName || ''}` : 'N/A'
  },
  interest_rate: ({ loan }) => {
    const rate = loan?.loanDetails?.interestRate || 0
    return `${rate.toFixed(2)}(M) ${(rate * 12).toFixed(2)}(Y)`
  },
  tenure_count: ({ loan }) => loan?.tenure || loan?.loanDetails?.tenure || '-',
  sanction_date: ({ loan }) => {
    const date = loan?.loanDetails?.sanctionDate || loan?.createdAt
    return date ? dayjs(date).format('DD-MM-YYYY') : '-'
  },
  amount_sanctioned_repeat: ({ loan }) =>
    (loan?.loanDetails?.loanAmountSanctioned || loan?.loanAmountSanctioned || 0).toFixed(2),
  payment_mode: ({ loan }) => loan?.loanDetails?.loanDisbursements?.[0]?.paymentMode || 'N/A',
  receipt_no: ({ emi }) => emi?._id || '-',
  emi_amount_due: ({ emi }) => (emi?.emiAmount || 0).toFixed(2),
  due_date: ({ loan, emi }) => {
    if (emi?.dueDate) return dayjs(emi.dueDate).format('DD/MM/YYYY')
    const emiSchedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    if (emiSchedule.length)
      return dayjs(emiSchedule[emiSchedule.length - 1].dueDate).format('DD/MM/YYYY')
    return '-'
  },
  pending_days: ({ loan, emi }) => {
    let targetEmi = emi
    if (!targetEmi?.dueDate) {
      const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
      targetEmi = schedule
        .filter((e) => !e.isPaid)
        .sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0]
    }

    if (!targetEmi) return 0

    const overdueInfo = calculateOverdueDays(
      targetEmi?.dueDate,
      targetEmi?.emiStatus,
      targetEmi?.paymentDate,
      targetEmi?.emiAmount,
      targetEmi?.lateFeeType,
      targetEmi?.lateFeeValue
    )
    return overdueInfo?.isOverdue ? overdueInfo.days : 0
  },
  customer_contact: ({ loan, emi }) => emi?.customer?.crn || loan?.customer?.crn || '-',
  total_principal_due: ({ emi }) => (emi?.principalAmount || 0).toFixed(2),
  total_interest_due: ({ emi }) => (emi?.interestAmount || 0).toFixed(2),
  overdue_charges: ({ emi }) => (emi?.penaltyAmount || 0).toFixed(2),
  late_fee_amount: ({ emi }) => {
    const overdueInfo = calculateOverdueDays(
      emi?.dueDate,
      emi?.emiStatus,
      emi?.paymentDate,
      emi?.emiAmount,
      emi?.lateFeeType,
      emi?.lateFeeValue
    )
    return (overdueInfo?.lateFeeAmount || 0).toFixed(2)
  },
  target_amount: ({ rowData }) => (rowData?.target_amount || 0).toFixed(2),
  collected_amount: ({ rowData }) => (rowData?.collected_amount || 0).toFixed(2),
  achievement_percent: ({ rowData }) =>
    rowData?.target_amount
      ? ((rowData.collected_amount / rowData.target_amount) * 100).toFixed(2) + '%'
      : '0%',
  date: ({ rowData }) => rowData?.date || '-',
  branch: ({ loan, rowData }) => {
    if (rowData?.branch) return rowData.branch
    const branch = loan?.branchName || loan?.branch || loan?.loanDetails?.branch
    if (typeof branch === 'object') return branch.branchName || '-'
    // If it's a string ID, we don't have the branches list here to lookup the name,
    // so we'll hope it's already populated or returned as name from backend
    return branch || '-'
  },
  last_payment_date: ({ loan }) => {
    const emiSchedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const paid = emiSchedule.filter((e) => e.isPaid) || []
    if (paid.length === 0) return '-'
    const last = paid.sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))[0]
    return last?.paymentDate ? dayjs(last.paymentDate).format('DD/MM/YYYY') : '-'
  },
  bucket: ({ loan, emi }) => {
    let targetEmi = emi
    if (!targetEmi?.dueDate) {
      const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
      targetEmi = schedule
        .filter((e) => !e.isPaid)
        .sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0]
    }

    if (!targetEmi) return 'Current'

    const overdueInfo = calculateOverdueDays(
      targetEmi?.dueDate,
      targetEmi?.emiStatus,
      targetEmi?.paymentDate,
      targetEmi?.emiAmount,
      targetEmi?.lateFeeType,
      targetEmi?.lateFeeValue
    )
    if (!overdueInfo?.isOverdue) return 'Current'
    const days = overdueInfo.days
    if (days <= 30) return '1-30'
    if (days <= 60) return '31-60'
    if (days <= 90) return '61-90'
    return '90+'
  },
  loan_type: ({ loan }) => loan?.typeOfLoan || '-',
  emi_no: ({ emi }) => emi?.emiNo || '-',
  emi_status: ({ loan, emi }) => {
    if (emi?.emiStatus) return emi.emiStatus
    if (emi?.isPaid) return 'Paid'
    if (loan) {
      const schedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
      if (schedule.length > 0 && schedule.every((e) => e.isPaid)) return 'Closed'
    }
    return 'Unpaid'
  },
  principal_amount: ({ emi }) => (emi?.principalAmount || 0).toFixed(2),
  interest_amount: ({ emi }) => (emi?.interestAmount || 0).toFixed(2),
  penalty_amount: ({ emi }) => (emi?.penaltyAmount || 0).toFixed(2),
  customer_mobile: ({ loan, emi }) =>
    emi?.customer?.customerMobile?.[0] || loan?.customer?.customerMobile?.[0] || '-',
  amount_sanctioned: ({ loan }) =>
    (loan?.loanDetails?.loanAmountSanctioned || loan?.loanAmountSanctioned || 0).toFixed(2),
  outstanding_principal: ({ loan }) => {
    const emiSchedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    if (emiSchedule.length > 0) {
      const unpaidPrincipal = emiSchedule
        .filter((e) => !e.isPaid)
        .reduce((sum, e) => sum + (e.principalAmount || 0), 0)
      return unpaidPrincipal.toFixed(2)
    }
    return (loan?.loanDetails?.outstandingPrincipal || loan?.outstandingAmount || 0).toFixed(2)
  },
  processing_fee: ({ loan }) => {
    const sanctioned = loan?.loanDetails?.loanAmountSanctioned || 0
    const fees = loan?.loanDetails?.processingFee || []
    const totalShare = fees.reduce((sum, f) => sum + (f.share || 0), 0)
    return ((sanctioned * totalShare) / 100).toFixed(2)
  },
  net_disbursed: ({ loan }) => {
    const sanctioned = loan?.loanDetails?.loanAmountSanctioned || 0
    const fees = loan?.loanDetails?.processingFee || []
    const totalShare = fees.reduce((sum, f) => sum + (f.share || 0), 0)
    const feeAmount = (sanctioned * totalShare) / 100
    // Simplified: sanctioned - fee (add GST logic if available in data)
    return (sanctioned - feeAmount).toFixed(2)
  },
  disbursal_date: ({ loan }) => {
    const date = loan?.loanDetails?.loanDisbursements?.[0]?.date
    return date ? dayjs(date).format('DD/MM/YYYY') : '-'
  },
  tenure: ({ loan }) => (loan?.tenure || loan?.loanDetails?.tenure || '-') + ' Months',
  ownership_type: ({ loan }) => 'Individual',

  // Collection & Visit Report Mappers
  visit_title: ({ task }) => task?.taskTitle || '-',
  visit_status: ({ task }) => task?.taskStatus || '-',
  visit_started: ({ task }) =>
    task?.actualStartDateTime ? dayjs(task.actualStartDateTime).format('DD/MM/YYYY hh:mm A') : '-',
  visit_completed: ({ task }) =>
    task?.actualEndDateTime ? dayjs(task.actualEndDateTime).format('DD/MM/YYYY hh:mm A') : '-',
  visit_assigned_to: ({ task }) =>
    task?.assignedTo ? `${task.assignedTo.firstName} ${task.assignedTo.lastName || ''}` : 'N/A',
  meeting_outcome: ({ task }) => task?.meetingDetails?.meetingOutCome || '-',

  // Additional Collection Mappers
  product_info: ({ loan }) => loan?.productInfo || '-',
  case_start_date: ({ loan }) =>
    loan?.caseStartDate ? dayjs(loan.caseStartDate).format('DD/MM/YYYY hh:mm A') : '-',
  case_end_date: ({ loan }) =>
    loan?.caseEndDate ? dayjs(loan.caseEndDate).format('DD/MM/YYYY hh:mm A') : '-',
  tat: ({ loan }) => {
    if (loan?.caseStartDate && loan?.caseEndDate) {
      const diff = dayjs(loan.caseEndDate).diff(dayjs(loan.caseStartDate))
      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      return `${hours}h ${minutes}m`
    }
    return '-'
  },
  agent_id: ({ loan }) => {
    if (loan?.assignedTo && Array.isArray(loan.assignedTo) && loan.assignedTo.length > 0) {
      if (typeof loan.assignedTo[0] === 'object') {
        return loan.assignedTo.map((u) => u.employeeId || '-').join(', ')
      }
    }
    return loan?.createdBy?.employeeId || '-'
  },
  outcome: ({ loan }) => loan?.userDefinedCaseStatus || '-',
  crn: ({ loan }) => loan?.customer?.crn || '-',
  file_no: ({ loan }) => loan?.fileNo || loan?.caseNo || '-',
  cif_id: ({ loan }) => loan?.customer?.cifId || loan?.customer?.crn || '-',
  company_name: ({ loan }) => loan?.company || 'Zavron',
  centre_name: ({ loan }) => loan?.centreName || '-',
  loan_category: ({ loan }) => loan?.loanCategory || 'General',
  purpose_of_loan: ({ loan }) => loan?.purposeOfLoan || '-',
  customer_no: ({ loan }) => loan?.customer?.customerMobile?.[0] || loan?.customer?.crn || '-',
  customer_email: ({ loan }) => loan?.customer?.customerEmail || '-',
  dues: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs())).length
  },
  due_amount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
    return overdue.reduce((sum, e) => sum + (e.emiAmount || 0), 0).toFixed(2)
  },
  pending_emi_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule
      .filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
      .sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))
    return overdue[0] ? dayjs(overdue[0].dueDate).format('DD-MM-YYYY') : '-'
  },
  language: () => 'English',
  guarantor_name: ({ loan }) =>
    loan?.guarantorName || loan?.loanDetails?.guarantors?.[0]?.name || '-',
  guarantor_no: ({ loan }) =>
    loan?.guarantorNo || loan?.loanDetails?.guarantors?.[0]?.mobile || '0',
  co_borrower_name: ({ loan }) =>
    loan?.coBorrowerName || loan?.loanDetails?.coApplicants?.[0]?.name || '-',
  co_borrower_cont_no: ({ loan }) =>
    loan?.coBorrowerContNo || loan?.loanDetails?.coApplicants?.[0]?.mobile || '-',
  first_due_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule[0]?.dueDate ? dayjs(schedule[0].dueDate).format('DD-MM-YYYY') : '-'
  },
  frequency: ({ loan }) =>
    loan?.loanDetails?.tenureType || loan?.loanDetails?.frequency || 'Monthly',
  total_amt: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const total = schedule.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    return total > 0 ? total.toFixed(2) : '-'
  },
  first_installment: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule[0]?.emiAmount ? schedule[0].emiAmount.toFixed(2) : '-'
  },
  indicative_roi: ({ loan }) => (loan?.loanDetails?.interestRate || 0).toFixed(2),
  total_finance: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.reduce((sum, e) => sum + (e.emiAmount || 0), 0).toFixed(2)
  },
  region: ({ loan }) => loan?.region || 'Default Region',
  emi_type: ({ loan }) => loan?.loanDetails?.emiType || 'Diminishing',
  cumm_vas: ({ loan }) => (loan?.cummVas || 0).toFixed(2),
  vas_collected: ({ loan }) => (loan?.vasCollected || 0).toFixed(2),
  vas_due: ({ loan }) => (loan?.vasDue || 0).toFixed(2),
  cumm_handloan: ({ loan }) => (loan?.cummHandloan || 0).toFixed(2),
  handloan_collected: ({ loan }) => (loan?.handloanCollected || 0).toFixed(2),
  handloan_due: ({ loan }) => (loan?.handloanDue || 0).toFixed(2),
  comm_enabled: ({ loan }) => (loan?.communicationEnabled ? 'Yes' : 'No'),
  legal: ({ loan }) => (loan?.isLegalStatus ? 'Yes' : '-'),
  funding_type: ({ loan }) => loan?.fundingType || 'Self',
  last_emi_paid: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const paid = schedule
      .filter((e) => e.isPaid)
      .sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))
    return paid[0]?.paymentDate ? dayjs(paid[0].paymentDate).format('DD-MM-YYYY') : '-'
  },
  arrear_received: () => '0.00',
  prev_adv_coll: () => '0.00',
  month_tbc: () => '0.00',
  month_tbc_received: () => '0.00',
  month_collected: () => '0.00',
  gross_tbc: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
    return overdue.reduce((sum, e) => sum + (e.emiAmount || 0), 0).toFixed(2)
  },
  gross_demand: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.reduce((sum, e) => sum + (e.emiAmount || 0), 0).toFixed(2)
  },
  gross_collected: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule
      .filter((e) => e.isPaid)
      .reduce((sum, e) => sum + (e.emiAmount || 0), 0)
      .toFixed(2)
  },
  coll_demand_pct: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const total = schedule.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    const collected = schedule
      .filter((e) => e.isPaid)
      .reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    return total > 0 ? ((collected / total) * 100).toFixed(2) : '0.00'
  },
  latest_status: ({ loan }) => {
    if (loan?.status === 'CLOSED') return 'Closed'
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const expiringDate = schedule.length > 0 ? dayjs(schedule[schedule.length - 1].dueDate) : null
    if (expiringDate && expiringDate.isBefore(dayjs())) return 'Expired'
    return loan?.status || 'Active'
  },
  effective_apr: ({ loan }) =>
    (loan?.loanDetails?.annualProcessingRate || loan?.loanDetails?.effectiveAPR || 0).toFixed(2),
  irr: ({ loan }) => (loan?.loanDetails?.irr || 0).toFixed(4),
  deductions: ({ loan }) => (loan?.loanDetails?.deductions || 0).toFixed(2),
  source_type: ({ loan }) =>
    loan?.sourceType || (loan?.loanDetails?.partner ? 'Partner' : 'Direct'),
  source_name: ({ loan }) => loan?.sourceName || loan?.loanDetails?.partner?.partnerName || '-',
  date_of_incorporation: ({ loan }) =>
    loan?.dateOfIncorporation || loan?.createdAt
      ? dayjs(loan.dateOfIncorporation || loan.createdAt).format('DD-MM-YYYY')
      : '-',
  collection_executive: ({ loan }) => {
    if (loan?.assignedTo && Array.isArray(loan.assignedTo) && loan.assignedTo.length > 0) {
      const exec = loan.assignedTo[0]
      if (typeof exec === 'object' && exec !== null) {
        return `${exec.firstName || ''} ${exec.lastName || ''}`.trim() || '-'
      }
      return exec // Return ID if not populated
    }
    return '-'
  },
  lead_owner: ({ loan }) => {
    if (loan?.leadOwner) return loan.leadOwner
    if (loan?.createdBy && typeof loan.createdBy === 'object') {
      return `${loan.createdBy.firstName || ''} ${loan.createdBy.lastName || ''}`.trim() || '-'
    }
    return '-'
  },
  renewed: ({ loan }) => (loan?.isRenewed ? 'Yes' : 'No'),
  product: ({ loan }) =>
    loan?.product || loan?.loanDetails?.productName || loan?.typeOfLoan || loan?.caseType || '-',
  customer_state: ({ loan }) => loan?.customer?.state || '-',
  area: ({ loan }) => loan?.customer?.area || '-',
  landmark: ({ loan }) => loan?.customer?.landmark || '-',
  loan_segment: ({ loan }) => loan?.loanSegment || 'General',
  scheme_name: ({ loan }) => loan?.loanDetails?.schemeName || '-',
  sanction_number: ({ loan }) => loan?.loanDetails?.sanctionNumber || loan?.caseNo || '-',
  simple_interest: ({ loan }) => {
    const rate = loan?.loanDetails?.interestRate || 0
    return `${rate.toFixed(2)}(M) ${(rate * 12).toFixed(2)}(Y)`
  },
  dealer_name: ({ loan }) => loan?.dealerName || '-',
  last_due_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.length > 0
      ? dayjs(schedule[schedule.length - 1].dueDate).format('DD-MM-YYYY')
      : '-'
  },
  last_installment_due_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.length > 0
      ? dayjs(schedule[schedule.length - 1].dueDate).format('DD-MM-YYYY')
      : '-'
  },
  disbursement_amount: ({ loan }) =>
    (
      loan?.loanDetails?.loanDisbursements?.[0]?.amount ||
      loan?.loanDetails?.totalAmountDisbursed ||
      0
    ).toFixed(2),
  disbursement_date: ({ loan }) => {
    const date = loan?.loanDetails?.loanDisbursements?.[0]?.date || loan?.emiStartDate
    return date ? dayjs(date).format('DD-MM-YYYY') : '-'
  },
  disbursement_status: ({ loan }) =>
    loan?.loanDetails?.disbursalStatus || loan?.loanDetails?.loanDisbursements?.[0]?.status || '-',
  credit_score: ({ loan }) => loan?.creditScore || loan?.loanDetails?.creditScore || '0',
  ckyc_id: ({ loan }) => loan?.ckycId || '-',
  finance_tags: ({ loan }) => loan?.financeTags || '-',
  floating_rate: ({ loan }) => (loan?.isFloatingRate ? 'Yes' : 'No'),
  loan_insurance_amount: ({ loan }) => (loan?.insuranceAmount || 0).toFixed(2),
  loan_insurance_tenure: ({ loan }) => loan?.insuranceTenure || '0',

  // Detailed Due Report specific mappers
  customer_address: ({ loan }) => {
    if (loan?.customer?.customerAddress && Array.isArray(loan.customer.customerAddress)) {
      const addr = loan.customer.customerAddress[0]
      if (addr) {
        return `${addr.addressLine1 || ''} ${addr.addressLine2 || ''} ${addr.city || ''} ${
          addr.state || ''
        } ${addr.pinCode || ''}`.trim()
      }
    }
    return '-'
  },
  alternate_no: ({ loan }) =>
    loan?.customer?.references?.[0]?.mobile || loan?.customer?.alternateMobile || '-',
  district: ({ loan }) => loan?.customer?.district || '-',
  gender: ({ loan }) => loan?.customer?.gender || '-',
  loan_value: ({ loan }) => (loan?.loanDetails?.loanAmountRequested || 0).toFixed(2),
  guarantor_address: ({ loan }) => {
    const g = loan?.loanDetails?.guarantors?.[0]
    if (g?.address) return g.address
    return '-'
  },
  bank_name: ({ loan }) =>
    loan?.loanDetails?.loanDisbursements?.[0]?.bankDetails?.bankName ||
    loan?.loanDetails?.partner?.partnerName ||
    '-',
  cheque_no: ({ loan }) =>
    loan?.loanDetails?.loanDisbursements?.[0]?.referenceNo ||
    loan?.loanDetails?.loanDisbursements?.[0]?.chequeDetails?.chequeNumber ||
    '-',
  down_payment: ({ loan }) => (loan?.loanDetails?.downPayment || 0).toFixed(2),
  monthly_tbc: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const totalDue = schedule.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    return totalDue.toFixed(2)
  },
  installment_dues: ({ emi }) => (emi?.emiAmount || 0).toFixed(2),
  advance_emi: ({ loan }) => (loan?.loanDetails?.advanceEmi || 0).toFixed(2),
  lcc_percent: ({ loan }) => (loan?.loanDetails?.latePaymentFee || 0).toFixed(2),
  loan_cycle_no: ({ loan }) => loan?.loanCycleNo || '1',
  ltv: ({ loan }) => (loan?.ltv || 0).toFixed(2),
  arrears_count: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const arrears = schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
    return arrears.length
  },
  arrears_amount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const arrears = schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
    const total = arrears.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    return total.toFixed(2)
  },
  overdue_principal: ({ emi }) => {
    if (!emi?.isPaid && dayjs(emi?.dueDate).isBefore(dayjs())) {
      return (emi.principalAmount || 0).toFixed(2)
    }
    return '0.00'
  },
  overdue_interest: ({ emi }) => {
    if (!emi?.isPaid && dayjs(emi?.dueDate).isBefore(dayjs())) {
      return (emi.interestAmount || 0).toFixed(2)
    }
    return '0.00'
  },
  excess_principal: () => '0.00',
  excess_interest: () => '0.00',
  insurance_company: ({ loan }) => loan?.insuranceCompany || '-',
  policy_branch: ({ loan }) => loan?.policyBranch || '-',
  id_proof: ({ loan }) => loan?.customer?.idProof || '-',
  insurance_expiry: ({ loan }) =>
    loan?.insuranceExpiry ? dayjs(loan.insuranceExpiry).format('DD-MM-YYYY') : '-',
  insurance_policy_no: ({ loan }) => loan?.insurancePolicyNo || '-',
  balance_outstanding: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const unpaid = schedule.filter((e) => !e.isPaid)
    const total = unpaid.reduce((sum, e) => sum + (e.principalAmount || 0), 0)
    return total.toFixed(2)
  },
  employment_type: ({ loan }) => loan?.customer?.employmentType || '-',
  cumulative_demand: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const dueTillDate = schedule.filter((e) => dayjs(e.dueDate).isBefore(dayjs()))
    const total = dueTillDate.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    return total.toFixed(2)
  },
  cumulative_collection: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const collected = schedule.filter((e) => e.isPaid)
    const total = collected.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    return total.toFixed(2)
  },
  future_flow: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const future = schedule.filter((e) => dayjs(e.dueDate).isAfter(dayjs()))
    const total = future.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    return total.toFixed(2)
  },
  npa_status: ({ loan }) => loan?.npaStatus || 'N',
  security_value: ({ loan }) => (loan?.securityValue || 0).toFixed(2),
  vas_dues: ({ loan }) => (loan?.vasDues || 0).toFixed(2),

  // PAR Report specific mappers
  amount_not_due: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const future = schedule.filter((e) => dayjs(e.dueDate).isAfter(dayjs()))
    const total = future.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    return total.toFixed(2)
  },
  par_1_29: ({ loan }) => getParBucketValue(loan, 1, 29),
  par_30_59: ({ loan }) => getParBucketValue(loan, 30, 59),
  par_60_89: ({ loan }) => getParBucketValue(loan, 60, 89),
  par_90_119: ({ loan }) => getParBucketValue(loan, 90, 119),
  par_120_149: ({ loan }) => getParBucketValue(loan, 120, 149),
  par_150_179: ({ loan }) => getParBucketValue(loan, 150, 179),
  par_180_365: ({ loan }) => getParBucketValue(loan, 180, 365),
  par_above_365: ({ loan }) => getParBucketValue(loan, 366, 99999),
  total_par: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
    const total = overdue.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    return total.toFixed(2)
  },

  // EMI Due Report specific mappers
  father_spouse: ({ loan }) => loan?.customer?.fatherSpouseName || 'NA',
  dues_count: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const arrears = schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
    const totalDue = arrears.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    const instAmt = schedule[0]?.emiAmount || 1
    return (totalDue / instAmt).toFixed(2)
  },
  lpc_amount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    // Sum of all penalty/late fees
    const total = schedule.reduce((sum, e) => sum + (e.penaltyAmount || 0), 0)
    return total > 0 ? total.toFixed(2) : '0'
  },
  handloan: () => '0',
  last_payment_amount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const paid = schedule
      .filter((e) => e.isPaid)
      .sort((a, b) => dayjs(b.paymentDate).diff(dayjs(a.paymentDate)))
    return paid[0]?.emiAmount ? paid[0].emiAmount.toFixed(2) : '0'
  },
  city: ({ loan }) => loan?.customer?.city || '-',
  consultant: ({ loan }) => loan?.consultant || '-',
  installments_received: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.filter((e) => e.isPaid).length
  },
  balance_emis: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.filter((e) => !e.isPaid).length
  },
  nach_regd: ({ loan }) =>
    loan?.isNachRegistered || loan?.loanDetails?.nachRegistered ? 'Yes' : 'No',
  pdc_available: ({ loan }) =>
    loan?.isPdcAvailable || loan?.loanDetails?.pdcAvailable ? 'Yes' : 'No',

  // Collection Report specific mappers
  channel: ({ emi }) => emi?.paymentChannel || emi?.paymentMethod || 'Deposit',
  instrument_no: ({ emi }) => emi?.referenceNo || emi?.transactionId || '-',
  ledger_name: () => 'CF_zavron_RP',
  collection_charges: () => '0',
  group_name: () => '-',
  partial_emi_no: () => '-',
  partial_emi_date: () => '-',
  partial_amount: () => '0',
  full_amount: () => '0',
  fin_status: ({ loan }) => loan?.status || '-',
  settlement_amount: () => '0',
  branch_prefix: ({ loan }) => {
    const no = loan?.caseNo || loan?.applicationNumber || ''
    return no.substring(0, 5) || 'SaleF'
  },
  roi_yearly: ({ loan }) => {
    const rate = loan?.loanDetails?.interestRate || 0
    return `${(rate * 12).toFixed(2)}(Y)`
  },
  accepted_at: ({ emi }) => {
    // Usually one day after payment or same day
    return emi?.paymentDate ? dayjs(emi.paymentDate).add(1, 'day').format('DD-MM-YYYY HH:mm') : '-'
  },
  foreclosure_charges: () => '0.00',
  bounce_charges: ({ emi }) => (emi?.penaltyAmount || 0).toFixed(2),

  // DPD Report Specific Mappers
  centre: ({ loan }) => loan?.loanDetails?.centreName || loan?.centreName || '-',
  customer_mobile_dpd: ({ loan }) => loan?.customer?.customerMobile?.[0] || '-',
  source: ({ loan }) => loan?.loanDetails?.sourceType || loan?.sourceType || 'Customer',
  source_name_dpd: ({ loan }) => loan?.loanDetails?.sourceName || loan?.sourceName || '-',
  sub_source: ({ loan }) => loan?.loanDetails?.subSourceName || loan?.subSourceName || '-',
  interest_type: ({ loan }) => loan?.loanDetails?.typeOfInterest || 'Diminishing',
  first_installment_amount: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return (schedule[0]?.emiAmount || 0).toFixed(2)
  },
  installment_start_date: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule[0]?.dueDate ? dayjs(schedule[0].dueDate).format('DD-MM-YYYY') : '-'
  },
  installments_received_on_time: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    return schedule.filter((e) => e.isPaid && !dayjs(e.paymentDate).isAfter(dayjs(e.dueDate)))
      .length
  },
  current_dpd: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
    if (!overdue.length) return 0
    const oldest = overdue.sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0]
    return dayjs().diff(dayjs(oldest.dueDate), 'day')
  },
  cumulative_overdue: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const overdue = schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
    return overdue.reduce((sum, e) => sum + (e.emiAmount || 0), 0).toFixed(2)
  },
  // Balance Sheet Mappers (Horizontal)
  le_particulars: ({ le_particulars }) => le_particulars || '',
  le_amount: ({ le_amount }) => le_amount || '',
  assets_particulars: ({ assets_particulars }) => assets_particulars || '',
  assets_amount: ({ assets_amount }) => assets_amount || '',

  bs_category: ({ category }) => category || '',
  bs_particulars: ({ particulars }) => particulars || '',
  bs_assets: ({ income, amount }) => {
    const val = amount !== undefined ? amount : income
    return val !== null && val !== 0 ? (val < 0 ? val.toFixed(2) : val.toFixed(2)) : ''
  },
  bs_liabilities: ({ expense }) => {
    return expense !== null && expense !== 0 ? expense.toFixed(2) : ''
  },
  cumulative_overdue_collected: ({ loan }) => {
    const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
    const collectedOverdue = schedule.filter(
      (e) => e.isPaid && dayjs(e.paymentDate).isAfter(dayjs(e.dueDate))
    )
    return collectedOverdue.reduce((sum, e) => sum + (e.emiAmount || 0), 0).toFixed(2)
  },

  // Trial Balance Mappers
  tb_group_name: ({ group }) => group?.name || '',
  tb_ledger_name: ({ ledger }) => ledger?.name || '',
  tb_debit: ({ summary }) =>
    summary?.totalDebit === null ? '' : (summary?.totalDebit || 0).toFixed(2),
  tb_credit: ({ summary }) =>
    summary?.totalCredit === null ? '' : (summary?.totalCredit || 0).toFixed(2),
  tb_closing_balance: ({ summary }) => {
    if (summary?.totalDebit === null) return ''
    const balance = (summary?.totalDebit || 0) - (summary?.totalCredit || 0)
    return balance.toFixed(2)
  },

  // P&L Mappers
  pnl_category: ({ category }) => category || '',
  pnl_particulars: ({ particulars, particulate }) => particulars || particulate || '',
  pnl_income: ({ type, amount, income }) => {
    if (income !== undefined && income !== null) return income !== 0 ? income.toFixed(2) : ''
    return type === 'INCOME' ? (amount || 0).toFixed(2) : ''
  },
  pnl_expense: ({ type, amount, expense }) => {
    if (expense !== undefined && expense !== null) return expense !== 0 ? expense.toFixed(2) : ''
    return type === 'EXPENSE' ? (amount || 0).toFixed(2) : ''
  },
}

// Helper for PAR buckets
function getParBucketValue(loan, min, max) {
  const schedule = loan?.emiSchedule || loan?.loanDetails?.emiSchedule || []
  const overdue = schedule.filter((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
  if (!overdue.length) return '0.00'

  // Calculate oldest DPD
  const oldest = overdue.sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0]
  const dpd = dayjs().diff(dayjs(oldest.dueDate), 'day')

  if (dpd >= min && dpd <= max) {
    const totalArrears = overdue.reduce((sum, e) => sum + (e.emiAmount || 0), 0)
    return totalArrears.toFixed(2)
  }
  return '0.00'
}

const CIBIL_HEADERS = [
  '[PN] Name Segment Tag',
  '[PN] Consumer Name Tag',
  '[PN] Consumer Name Field',
  '[PN] DOB Tag',
  '[PN] Date of Birth',
  '[PN] Gender Tag',
  '[PN] Gender',

  '[ID] ID Segment Tag',
  '[ID] ID Type',
  '[ID] ID Number Tag',
  '[ID] ID Number',

  '[PT] Phone Segment Tag',
  '[PT] Cell Tag',
  '[PT] Telephone Number',
  '[PT] Telephone Type',

  '[EC] Email Segment Tag',
  '[EC] E-Mail Tag',
  '[EC] Email ID',

  '[PA] Address Segment Tag',
  '[PA] Address Line1 Tag',
  '[PA] Address Line1',
  '[PA] Address Line2 Tag',
  '[PA] Address Line2',
  '[PA] Address Line3 Tag',
  '[PA] Address Line3',
  '[PA] Address Line4 Tag',
  '[PA] Address Line4',
  '[PA] Address Pin',
  '[PA] State Code Tag',
  '[PA] State Code',
  '[PA] PIN Code Tag',
  '[PA] PIN Code',
  '[PA] Address Category',

  '[TL] Account Segment Tag',
  '[TL] Member Code Tag',
  '[TL] Current/New Reporting Member Code',
  '[TL] Member Name Tag',
  '[TL] Current/New Member Short Name',
  '[TL] card seg Tag',
  '[TL] Account No',
  '[TL] Account Type',
  '[TL] Member type Tag',
  '[TL] Member Type',
  '[TL] Date Opened/Disbursed Tag',
  '[TL] Date Opened/Disbursed',
  '[TL] Last Payment Date Tag',
  '[TL] Last Payment Date',
  '[TL] Date Closed Tag',
  '[TL] Date Closed',
  '[TL] Date Reported and Certified Tag',
  '[TL] Date Reported and Certified',
  '[TL] Sanctioned Amount Tag',
  '[TL] High Credit/Sanctioned Amount',
  '[TL] Current Balance Tag',
  '[TL] Current Balance',
  '[TL] Amount Overdue Tag',
  '[TL] Amount Overdue',
  '[TL] Past Due Tag',
  '[TL] Number of Days Past Due',
  '[TL] Old Member Code Tag',
  '[TL] Old Member Code',
  '[TL] Old Member Short Name Tag',
  '[TL] Old Member Short Name',
  '[TL] Old Account Tag',
  '[TL] Old Account Number',
  '[TL] Credit Facility Tag',
  '[TL] Credit Facility',
  '[TL] Asset Tag',
  '[TL] Asset',
  '[TL] Security Amount Tag',
  '[TL] Security Amount',
  '[TL] Security Type Tag',
  '[TL] Security Type',
  '[TL] Cr Dr Limit Tag',
  '[TL] Cr Dr Limit',
  '[TL] Cash Limit Tag',
  '[TL] Cash Limit',
  '[TL] Written-off Amount Tag',
  '[TL] Written-off Amount',
  '[TL] Writeoff Principal Amount Tag',
  '[TL] Writeoff Principal Amount',
  '[TL] Settlement Amount Tag',
  '[TL] Settlement Amount',
  '[TL] Pay Frequency Tag',
  '[TL] Pay Frequency',
  '[TL] Occupation Tag',
  '[TL] Occupation',

  '[ES] End Segment'
]

export const REPORT_COLUMNS = {
  'Loan Collection Report': {
    getRows: (loans) => {
      const rows = []
      loans.forEach((loan) => {
        const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
        const paidEmis = emiSchedule.filter((e) => e.isPaid) || []
        paidEmis.forEach((emi) => rows.push({ loan, emi }))
      })
      return rows
    },
    columns: [
      { text: 'Collection Date', value: 'collection_date' },
      { text: 'Agent Name', value: 'agent_name' },
      { text: 'Payment Mode', value: 'payment_mode' },
      { text: 'Branch', value: 'branch' },
      { text: 'Status', value: 'emi_status' },
      { text: 'Receipt No', value: 'receipt_no' }
    ]
  },
  'Daily Collection Status Report': {
    getRows: (loans) => {
      const agentSummary = {}
      loans.forEach((loan) => {
        const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
        const paidEmis = emiSchedule.filter((e) => e.isPaid) || []
        paidEmis.forEach((emi) => {
          const agentId = loan.assignedTo?.[0] || 'Unassigned'
          const date = emi.paymentDate ? dayjs(emi.paymentDate).format('DD/MM/YYYY') : 'N/A'
          const key = `${agentId}_${date}`
          if (!agentSummary[key]) {
            agentSummary[key] = {
              rowData: {
                agent_name: loan.createdBy
                  ? `${loan.createdBy.firstName} ${loan.createdBy.lastName || ''}`
                  : 'N/A',
                date: date,
                collected_amount: 0,
                target_amount: 100000,
                branch: 'Head Office'
              }
            }
          }
          agentSummary[key].rowData.collected_amount += emi.emiAmount
        })
      })
      return Object.values(agentSummary)
    },
    columns: [
      { text: 'Agent Name', value: 'agent_name' },
      { text: 'Target Amount', value: 'target_amount' },
      { text: 'Collected Amount', value: 'collected_amount' },
      { text: 'Achievement %', value: 'achievement_percent' },
      { text: 'Date', value: 'date' },
      { text: 'Branch', value: 'branch' }
    ]
  },
  'EMI DUE Report': {
    getRows: (loans) => {
      const rows = []
      loans.forEach((loan) => {
        const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
        const unpaidEmis = emiSchedule.filter((e) => !e.isPaid) || []
        unpaidEmis.forEach((emi) => rows.push({ loan, emi }))
      })
      return rows
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Contact', value: 'customer_contact' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'EMI No', value: 'emi_no' },
      { text: 'EMI Amount Due', value: 'emi_amount_due' },
      { text: 'Due Date', value: 'due_date' },
      { text: 'Pending Days', value: 'pending_days' },
      { text: 'Asset Category', value: 'asset_category' },
      { text: 'Provisioning Amount', value: 'provisioning_amount' }
    ]
  },
  'Detailed Due Report': {
    getRows: (loans) => {
      // Return one row per loan that has overdue EMIs
      return loans
        .filter((loan) => {
          const schedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
          return schedule.some((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
        })
        .map((loan) => ({ loan, emi: {} }))
    },
    columns: [
      { text: 'Branch', value: 'branch' },
      { text: 'Centre', value: 'centre_name' },
      { text: 'Loan Number', value: 'loan_account_no' },
      { text: 'CIF Id', value: 'cif_id' },
      { text: 'Loan Date', value: 'sanction_date' },
      { text: 'Father/Spouse', value: 'father_spouse' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_no' },
      { text: 'Dues', value: 'dues_count' },
      { text: 'Due Amount', value: 'arrears_amount' },
      { text: '* LPC', value: 'lpc_amount' },
      { text: 'VAS Dues', value: 'vas_dues' },
      { text: 'Handloan', value: 'handloan' },
      { text: 'Last Paid Date', value: 'last_payment_date' },
      { text: 'Last Paid Amount', value: 'last_payment_amount' },
      { text: 'Frequency', value: 'frequency' },
      { text: 'Address', value: 'customer_address' },
      { text: 'Area', value: 'area' },
      { text: 'City', value: 'city' },
      { text: 'State', value: 'customer_state' },
      { text: 'Consultant', value: 'consultant' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Product', value: 'product' },
      { text: 'Installment Amount', value: 'first_installment' },
      { text: 'Tenure', value: 'tenure_count' },
      { text: 'Loan Amount', value: 'amount_sanctioned' },
      { text: 'No. Of Installments Received', value: 'installments_received' },
      { text: 'Balance EMIs', value: 'balance_emis' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Scheme Name', value: 'scheme_name' },
      { text: 'NACH Regd', value: 'nach_regd' },
      { text: 'PDC Available', value: 'pdc_available' },
      { text: 'Sanction Number', value: 'sanction_number' },
      { text: 'Asset Category', value: 'asset_category' },
      { text: 'Provisioning Amount', value: 'provisioning_amount' }
    ]
  },
  'Non Starter Report': {
    getRows: (loans) => {
      const rows = []
      loans.forEach((loan) => {
        const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
        if (!emiSchedule.length) return

        // Find the first EMI installment
        const firstEmi = emiSchedule.find((e) => e.emiNo === 1) || emiSchedule[0]

        // If the first EMI is unpaid, this is a non-starter loan
        if (firstEmi && !firstEmi.isPaid) {
          rows.push({ loan, emi: firstEmi })
        }
      })
      return rows
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'EMI Start Date', value: 'due_date' },
      { text: 'EMI Amount', value: 'emi_amount_due' },
      { text: 'Status', value: 'emi_status' }
    ]
  },
  'Loan Expiry Report': {
    getRows: (loans) => {
      const today = dayjs()
      return loans
        .filter((loan) => {
          const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
          if (!emiSchedule.length) return false
          const lastDate = dayjs(emiSchedule[emiSchedule.length - 1].dueDate)
          const hasDues = emiSchedule.some((e) => !e.isPaid)
          return lastDate.isBefore(today) && hasDues
        })
        .map((loan) => ({ loan, emi: {} })) // Map to loan structure
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'Expiry Date (Last Due)', value: 'due_date' },
      { text: 'Total Sanctioned', value: 'amount_sanctioned' },
      { text: 'Outstanding Principal', value: 'outstanding_principal' },
      { text: 'Bucket', value: 'bucket' },
      { text: 'Last Payment Date', value: 'last_payment_date' }
    ]
  },
  'VAS Report': {
    apiType: 'emi-details',
    getRows: (emis) => emis.map((emi) => ({ loan: emi.loan, emi })),
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Bounce Charges', value: 'penalty_amount' },
      { text: 'Late Payment Charges', value: 'late_fee_amount' },
      { text: 'Total Extra Charges', value: 'overdue_charges' }
    ]
  },
  'Future payment report': {
    apiType: 'emi-details',
    getRows: (emis) => {
      const today = dayjs()
      return emis
        .filter((emi) => !emi.isPaid && dayjs(emi.dueDate).isAfter(today))
        .map((emi) => ({ loan: emi.loan, emi }))
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Future Due Date', value: 'due_date' },
      { text: 'Estimated EMI', value: 'emi_amount_due' }
    ]
  },
  'Loan PAR Report': {
    getRows: (loans) => {
      return loans
        .filter((loan) => {
          const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
          return emiSchedule.some((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
        })
        .map((loan) => ({ loan, emi: {} }))
    },
    columns: [
      { text: 'Loan No', value: 'loan_account_no' },
      { text: 'CIF Id', value: 'cif_id' },
      { text: 'Sanction Number', value: 'sanction_number' },
      { text: 'Payment Mode', value: 'payment_mode' },
      { text: 'Lead Owner', value: 'lead_owner' },
      { text: 'Executive', value: 'collection_executive' },
      { text: 'Borrower Name', value: 'customer_name' },
      { text: 'Location', value: 'centre_name' },
      { text: 'State', value: 'customer_state' },
      { text: 'Dis Date', value: 'disbursement_date' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Principal', value: 'amount_sanctioned' },
      { text: 'Loan Amount', value: 'amount_sanctioned_repeat' },
      { text: 'Amount Not Due', value: 'amount_not_due' },
      { text: '1-29', value: 'par_1_29' },
      { text: '30-59', value: 'par_30_59' },
      { text: '60-89', value: 'par_60_89' },
      { text: '90-119', value: 'par_90_119' },
      { text: '120-149', value: 'par_120_149' },
      { text: '150-179', value: 'par_150_179' },
      { text: '180-365', value: 'par_180_365' },
      { text: 'Above 365', value: 'par_above_365' },
      { text: 'Arrears', value: 'arrears_amount' },
      { text: 'Total PAR', value: 'total_par' },
      { text: 'Bucket', value: 'bucket' },
      { text: 'NPA', value: 'npa_status' },
      { text: 'Total DPD', value: 'pending_days' }
    ]
  },
  'EMI Tracker Report': {
    apiType: 'emi-details',
    getRows: (emis) => emis.map((emi) => ({ loan: emi.loan, emi })),
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'EMI No', value: 'emi_no' },
      { text: 'Due Date', value: 'due_date' },
      { text: 'EMI Status', value: 'emi_status' },
      { text: 'Amount Paid', value: 'amount_collected' },
      { text: 'Payment Date', value: 'collection_date' }
    ]
  },
  'Cash Inflow Report': {
    apiType: 'emi-details',
    getRows: (emis) => emis.filter((emi) => emi.isPaid).map((emi) => ({ loan: emi.loan, emi })),
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Principal Received', value: 'principal_amount' },
      { text: 'Interest Received', value: 'interest_amount' },
      { text: 'Late Fees/Bounce', value: 'overdue_charges' },
      { text: 'Total Received', value: 'amount_collected' },
      { text: 'Collection Date', value: 'collection_date' }
    ]
  },
  'Cash Outflow Report': {
    getRows: (loans) => loans.map((loan) => ({ loan, emi: {} })),
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Sanctioned Amount', value: 'amount_sanctioned' },
      { text: 'Disbursal Mode', value: 'payment_mode' },
      { text: 'Processing Fee', value: 'processing_fee' },
      { text: 'Net Disbursed', value: 'net_disbursed' },
      { text: 'Disbursal Date', value: 'disbursal_date' }
    ]
  },
  'Business Done Report': {
    getRows: (loans) => loans.map((loan) => ({ loan, emi: {} })),
    columns: [
      { text: 'File No.', value: 'file_no' },
      { text: 'Loan Number', value: 'loan_account_no' },
      { text: 'CIF Id', value: 'cif_id' },
      { text: 'Company', value: 'company_name' },
      { text: 'Branch', value: 'branch' },
      { text: 'Centre Name', value: 'centre_name' },
      { text: 'Entity Type', value: 'ownership_type' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Loan Category', value: 'loan_category' },
      { text: 'Purpose of Loan', value: 'purpose_of_loan' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer No.', value: 'customer_no' },
      { text: 'Guarantor Name', value: 'guarantor_name' },
      { text: 'Guarantor No.', value: 'guarantor_no' },
      { text: 'Co Borrower Name', value: 'co_borrower_name' },
      { text: 'Co Borrower ContNo.', value: 'co_borrower_cont_no' },
      { text: 'Loan Date', value: 'sanction_date' },
      { text: 'First Due Date', value: 'first_due_date' },
      { text: 'Installments', value: 'tenure_count' },
      { text: 'Frequency', value: 'frequency' },
      { text: 'Loan Amount', value: 'amount_sanctioned' },
      { text: 'Total Amt', value: 'total_amt' },
      { text: 'First Installment', value: 'first_installment' },
      { text: 'Interest (%)', value: 'interest_rate' },
      { text: 'IndicativeROI (%)', value: 'indicative_roi' },
      { text: 'Effective APR (%)', value: 'effective_apr' },
      { text: 'IRR', value: 'irr' },
      { text: 'Deductions', value: 'deductions' },
      { text: 'Source Type', value: 'source_type' },
      { text: 'Source Name', value: 'source_name' },
      { text: 'Date Of Incorporation', value: 'date_of_incorporation' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Lead Owner', value: 'lead_owner' },
      { text: 'Renewed', value: 'renewed' },
      { text: 'Product', value: 'product' },
      { text: 'Customer State', value: 'customer_state' },
      { text: 'Area', value: 'area' },
      { text: 'Landmark', value: 'landmark' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Scheme Name', value: 'scheme_name' },
      { text: 'Sanction Number', value: 'sanction_number' },
      { text: 'Simple Interest', value: 'simple_interest' },
      { text: 'Dealer Name', value: 'dealer_name' },
      { text: 'Last Due Date', value: 'last_due_date' },
      { text: 'Last Installment Due Date', value: 'last_installment_due_date' },
      { text: 'Disbursement Amount', value: 'disbursement_amount' },
      { text: 'Disbursement Date', value: 'disbursement_date' },
      { text: 'Disbursement Status', value: 'disbursement_status' },
      { text: 'Credit Score', value: 'credit_score' },
      { text: 'CKYC Id', value: 'ckyc_id' },
      { text: 'Loan Amount', value: 'amount_sanctioned_repeat' },
      { text: 'Finance Tags', value: 'finance_tags' },
      { text: 'Floating Rate', value: 'floating_rate' },
      { text: 'Loan Insurance Amount', value: 'loan_insurance_amount' },
      { text: 'Loan Insurance Tenure', value: 'loan_insurance_tenure' }
    ]
  },
  'Loan Closure Report': {
    getRows: (loans) => {
      return loans
        .filter((loan) => {
          const emiSchedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
          return emiSchedule.length > 0 && emiSchedule.every((e) => e.isPaid)
        })
        .map((loan) => ({ loan, emi: {} }))
    },
    columns: [
      { text: 'Loan Account No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Sanctioned Amount', value: 'amount_sanctioned' },
      { text: 'Closure Date', value: 'last_payment_date' },
      { text: 'Status', value: 'emi_status' }
    ]
  },
  'CIBIL TUDF Format': {
    getRows: (loans) => loans.map((loan) => ({ loan, emi: {} })),
    columns: CIBIL_HEADERS.map((h) => ({ text: h, value: h }))
  },
  'CIBIL UCRF Format': {
    getRows: (loans) => loans.map((loan) => ({ loan, emi: {} })),
    columns: CIBIL_HEADERS.map((h) => ({ text: h, value: h }))
  },
  'Attendance Report': {
    apiType: 'attendance',
    columns: [
      { text: 'Attendance', value: 'Attendance' },
      { text: 'First Clock-In', value: 'First Clock-In' },
      { text: 'Last Clock-Out', value: 'Last Clock-Out' },
      { text: 'Planned Working Hour', value: 'Planned Working Hour' },
      { text: 'Completed Working Hour', value: 'Completed Working Hour' },
      { text: 'Overtime', value: 'Overtime' },
      { text: 'Clock-In Image', value: 'Clock-In Image' },
      { text: 'Clock-Out Image', value: 'Clock-Out Image' },
      { text: 'Delay In Shift', value: 'Delay In Shift' },
      { text: 'Breaks Count', value: 'Breaks Count' },
      { text: 'Distance (km)', value: 'Distance (km)' }
    ]
  },
  'Muster Report-I': {
    apiType: 'attendance',
    columns: [
      { text: 'Attendance', value: 'Attendance' },
      { text: 'First Clock-In', value: 'First Clock-In' },
      { text: 'Last Clock-Out', value: 'Last Clock-Out' },
      { text: 'Working Hours', value: 'Working Hours' },
      { text: 'Overtime', value: 'Overtime' },
      { text: 'Delay In Shift', value: 'Delay In Shift' },
      { text: 'Breaks Count', value: 'Breaks Count' },
      { text: 'Distance (km)', value: 'Distance (km)' }
    ]
  },
  'Muster Report-II': {
    apiType: 'attendance',
    categories: [
      {
        name: 'Employee Details',
        columns: [
          { text: 'Employee Name', value: 'Employee Name' },
          { text: 'Emp Id', value: 'Emp Id' },
          { text: 'Department', value: 'Department' },
          { text: 'Designation', value: 'Designation' }
        ]
      },
      {
        name: 'Manager Details',
        columns: [
          { text: 'Direct Manager', value: 'Direct Manager' },
          { text: 'Manager Hierarchy', value: 'Manager Hierarchy' }
        ]
      },
      {
        name: 'Daily Attendance Reports',
        columns: [
          { text: 'Date', value: 'Date' },
          { text: 'Attendance', value: 'Attendance' },
          { text: 'First Clock-In', value: 'First Clock-In' },
          { text: 'Last Clock-Out', value: 'Last Clock-Out' },
          { text: 'Shift Start Time', value: 'Shift Start Time' },
          { text: 'Planned Working Hour', value: 'Planned Working Hour' },
          { text: 'Completed Working Hour', value: 'Completed Working Hour' },
          { text: 'Breaks Count', value: 'Breaks Count' },
          { text: 'Overtime', value: 'Overtime' },
          { text: 'Delay In Shift', value: 'Delay In Shift' },
          { text: 'Clock-In Image', value: 'Clock-In Image' },
          { text: 'Clock-Out Image', value: 'Clock-Out Image' }
        ]
      },
      {
        name: 'Activity Details',
        columns: [
          { text: 'Activity Date', value: 'Activity Date' },
          { text: 'Activity', value: 'Activity' },
          { text: 'Activity Start Time', value: 'Activity Start Time' },
          { text: 'Activity End Time', value: 'Activity End Time' },
          { text: 'Duration of Activity', value: 'Duration of Activity' },
          { text: 'Activity Start Location', value: 'Activity Start Location' },
          { text: 'Activity End Location', value: 'Activity End Location' }
        ]
      },
      {
        name: 'Visit Details',
        columns: [
          { text: 'Visit Date', value: 'Visit Date' },
          { text: 'Customer Name', value: 'Customer Name' },
          { text: 'Arrival Time', value: 'Arrival Time' },
          { text: 'Departure Time', value: 'Departure Time' },
          { text: 'Duration of Visit', value: 'Duration of Visit' },
          { text: 'Visit Location', value: 'Visit Location' },
          { text: 'Daily Total Visits', value: 'Daily Total Visits' },
          { text: 'Distance Breakup Per Day', value: 'Distance Breakup Per Day' },
          { text: 'Total Distance Per Day', value: 'Total Distance Per Day' },
          { text: 'Mode of Transportation', value: 'Mode of Transportation' },
          { text: 'Notes/Comments', value: 'Notes/Comments' }
        ]
      },
      {
        name: 'Monthly Travel Summary',
        columns: [
          { text: 'Month', value: 'Month' },
          { text: 'Total Distance Traveled', value: 'Total Distance Traveled' },
          { text: 'Total Visits Done', value: 'Total Visits Done' }
        ]
      }
    ],
    columns: [
      'Employee Name',
      'Emp Id',
      'Department',
      'Designation',
      'City',
      'Direct Manager',
      'Manager Hierarchy',
      'Date',
      'Attendance',
      'First Clock-In',
      'Last Clock-Out',
      'Shift Start Time',
      'Planned Working Hour',
      'Completed Working Hour',
      'Breaks Count',
      'Overtime',
      'Delay In Shift',
      'Clock-In Image',
      'Clock-Out Image',
      'Activity Date',
      'Activity',
      'Activity Start Time',
      'Activity End Time',
      'Duration of Activity',
      'Activity Start Location',
      'Activity End Location',
      'Visit Date',
      'Customer Name',
      'Arrival Time',
      'Departure Time',
      'Duration of Visit',
      'Visit Location',
      'Daily Total Visits',
      'Distance Breakup Per Day',
      'Total Distance Per Day',
      'Mode of Transportation',
      'Notes/Comments',
      'Month',
      'Total Distance Traveled',
      'Total Visits Done'
    ].map((c) => ({ text: c, value: c }))
  },
  'Visit Report': {
    apiType: 'collection-tasks',
    getRows: (tasks) => tasks.map((task) => ({ task, loan: task.case, emi: {} })),
    columns: [
      { text: 'Visit Title', value: 'visit_title' },
      { text: 'Case No', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Visit Status', value: 'visit_status' },
      { text: 'Started At', value: 'visit_started' },
      { text: 'Completed At', value: 'visit_completed' },
      { text: 'Assigned To', value: 'visit_assigned_to' },
      { text: 'Meeting Outcome', value: 'meeting_outcome' }
    ]
  },
  'Days Past Due Report': {
    getRows: (loans) => loans.map((loan) => ({ loan, emi: {} })),
    columns: [
      { text: 'Branch', value: 'branch' },
      { text: 'Centre', value: 'centre' },
      { text: 'Loan Number', value: 'loan_account_no' },
      { text: 'Loan Date', value: 'sanction_date' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile_dpd' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Source', value: 'source' },
      { text: 'Source Name', value: 'source_name_dpd' },
      { text: 'Sub Source Name', value: 'sub_source' },
      { text: 'Lead Owner', value: 'lead_owner' },
      { text: 'Loan Amount', value: 'amount_sanctioned' },
      { text: 'Interest Type', value: 'interest_type' },
      { text: 'Disbursement Amount', value: 'disbursement_amount' },
      { text: 'Disbursement Date', value: 'disbursement_date' },
      { text: 'First Installment Amount', value: 'first_installment_amount' },
      { text: 'Installment Start Date', value: 'installment_start_date' },
      { text: 'No. of Installments received(On Time)', value: 'installments_received_on_time' },
      { text: 'Current DPD', value: 'current_dpd' },
      { text: 'Cumulative Overdue', value: 'cumulative_overdue' },
      { text: 'Cumulative Overdue Collected', value: 'cumulative_overdue_collected' },
      { text: 'Collection Executive', value: 'collection_executive' }
    ]
  },
  'Overdue List Report': {
    getRows: (loans) => {
      return loans
        .filter((loan) => {
          const schedule = loan.emiSchedule || loan.loanDetails?.emiSchedule || []
          return schedule.some((e) => !e.isPaid && dayjs(e.dueDate).isBefore(dayjs()))
        })
        .map((loan) => ({ loan, emi: {} }))
    },
    columns: [
      { text: 'Company', value: 'company_name' },
      { text: 'Branch', value: 'branch' },
      { text: 'Centre', value: 'centre_name' },
      { text: 'Loan Number', value: 'loan_account_no' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Customer Mobile', value: 'customer_mobile' },
      { text: 'Customer Email', value: 'customer_email' },
      { text: 'Dues', value: 'dues' },
      { text: 'Due Amount', value: 'due_amount' },
      { text: 'Pending EMI Date', value: 'pending_emi_date' },
      { text: 'Language', value: 'language' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Scheme Name', value: 'scheme_name' }
    ]
  },
  'LCC Report': {
    getRows: (loans) => loans.map((loan) => ({ loan, emi: {} })),
    columns: [
      { text: 'Loan Number', value: 'loan_account_no' },
      { text: 'CIF Id', value: 'cif_id' },
      { text: 'Company', value: 'company_name' },
      { text: 'Branch', value: 'branch' },
      { text: 'Centre Name', value: 'centre_name' },
      { text: 'Entity Type', value: 'ownership_type' },
      { text: 'Region', value: 'region' },
      { text: 'Type', value: 'renewed' },
      { text: 'Loan Category', value: 'loan_category' },
      { text: 'Loan Type', value: 'loan_type' },
      { text: 'Loan Date', value: 'sanction_date' },
      { text: 'Customer Name', value: 'customer_name' },
      { text: 'Father/Spouse Name', value: 'father_spouse' },
      { text: 'Cust Mobile', value: 'customer_no' },
      { text: 'Alternate Contact No', value: 'alternate_no' },
      { text: 'Customer Address', value: 'customer_address' },
      { text: 'Guarantor', value: 'guarantor_name' },
      { text: 'Guarantor Mobile', value: 'guarantor_no' },
      { text: 'Guarantor Address', value: 'guarantor_address' },
      { text: 'Principal', value: 'amount_sanctioned' },
      { text: 'Loan Amount', value: 'amount_sanctioned_repeat' },
      { text: 'Total Finance', value: 'total_finance' },
      { text: 'Installments', value: 'tenure_count' },
      { text: 'Interest', value: 'interest_rate' },
      { text: 'Frequency', value: 'frequency' },
      { text: 'EMI Type', value: 'emi_type' },
      { text: 'First Due Date', value: 'first_due_date' },
      { text: 'Last Due Date', value: 'last_due_date' },
      { text: 'Installment Date', value: 'pending_emi_date' },
      { text: 'Area', value: 'area' },
      { text: 'Source', value: 'source_type' },
      { text: 'Source Name', value: 'source_name' },
      { text: 'Collection Executive', value: 'collection_executive' },
      { text: 'Latest Status', value: 'latest_status' },
      { text: 'Cont. IRR (%)', value: 'irr' },
      { text: 'Arrears', value: 'arrears_amount' },
      { text: 'Arrear Received', value: 'arrear_received' },
      { text: 'Prev Adv Coll', value: 'prev_adv_coll' },
      { text: 'Month TBC', value: 'month_tbc' },
      { text: 'Month TBC Received', value: 'month_tbc_received' },
      { text: 'Total Dues', value: 'due_amount' },
      { text: 'Month Collected', value: 'month_collected' },
      { text: 'Gross TBC', value: 'gross_tbc' },
      { text: 'Gross Demand', value: 'gross_demand' },
      { text: 'Gross Collected', value: 'gross_collected' },
      { text: 'Coll/Demand (%)', value: 'coll_demand_pct' },
      { text: 'Receipt Count', value: 'receipt_no' },
      { text: 'Advance Coll.', value: 'advance_emi' },
      { text: 'EMI Due Count', value: 'dues_count' },
      { text: 'Running EMI Count', value: 'emi_no' },
      { text: 'Paid Installments', value: 'installments_received' },
      { text: 'Balance Installments', value: 'balance_emis' },
      { text: 'LPC Dues', value: 'lpc_amount' },
      { text: 'LPC Discount', value: 'overdue_charges' },
      { text: 'Bucket', value: 'bucket' },
      { text: 'Last Rcvd Date', value: 'last_payment_date' },
      { text: 'Cumm VAS', value: 'cumm_vas' },
      { text: 'VAS Collected', value: 'vas_collected' },
      { text: 'VAS Due', value: 'vas_due' },
      { text: 'Cumm Handloan', value: 'cumm_handloan' },
      { text: 'Handloan Collected', value: 'handloan_collected' },
      { text: 'Handloan Due', value: 'handloan_due' },
      { text: 'Principal Outstanding', value: 'balance_outstanding' },
      { text: 'Principal Received', value: 'cumulative_collection' },
      { text: 'Interest Received', value: 'cumulative_collection' },
      { text: 'LPC Collected', value: 'overdue_charges' },
      { text: 'Date Of Incorporation', value: 'date_of_incorporation' },
      { text: 'Future Principal Dues', value: 'future_flow' },
      { text: 'Settlement Amount', value: 'settlement_amount' },
      { text: 'Loan Segment', value: 'loan_segment' },
      { text: 'Scheme Name', value: 'scheme_name' },
      { text: 'Total Pending Installments', value: 'dues_count' },
      { text: 'Sanction Number', value: 'sanction_number' },
      { text: 'Communication Enabled', value: 'comm_enabled' },
      { text: 'Legal', value: 'legal' },
      { text: 'Tagging Name', value: 'centre_name' },
      { text: 'Last Instalment Paid Date', value: 'last_payment_date' },
      { text: 'Last EMI Paid', value: 'last_emi_paid' },
      { text: 'Mode of RePayment', value: 'payment_mode' },
      { text: 'Funding Type', value: 'funding_type' },
      { text: 'Floating Rate', value: 'floating_rate' },
      { text: 'Interest Outstanding', value: 'overdue_interest' },
      { text: 'Asset Category', value: 'asset_category' },
      { text: 'Provisioning Amount', value: 'provisioning_amount' }
    ]
  },
  'Trial Balance Report': {
    apiType: 'trial-balance',
    getRows: (rawData) => {
      const { groups, summary } = rawData
      const rows = []
      groups.forEach((group) => {
        if (group.ledgers && group.ledgers.length > 0) {
          group.ledgers.forEach((ledger) => {
            const ledgerSum = summary.ledgerWise.find((l) => l._id === ledger._id) || {}
            rows.push({
              group,
              ledger,
              summary: ledgerSum
            })
          })
        }
      })

      // Calculate Totals
      if (rows.length > 0) {
        let totalDebit = 0
        let totalCredit = 0
        rows.forEach((r) => {
          totalDebit += r.summary?.totalDebit || 0
          totalCredit += r.summary?.totalCredit || 0
        })

        // Add empty rows for spacing
        for (let i = 0; i < 3; i++) {
          rows.push({
            group: { name: '' },
            ledger: { name: '' },
            summary: { totalDebit: null, totalCredit: null }
          })
        }

        // Add Summary Row
        rows.push({
          group: { name: '' },
          ledger: { name: 'GRAND TOTAL' },
          isSummary: true,
          summary: {
            totalDebit,
            totalCredit
          }
        })
      }

      return rows
    },
    columns: [
      { text: 'Group Name', value: 'tb_group_name' },
      { text: 'Ledger Name', value: 'tb_ledger_name' },
      { text: 'Debit Amount', value: 'tb_debit' },
      { text: 'Credit Amount', value: 'tb_credit' },
      { text: 'Closing Balance', value: 'tb_closing_balance' }
    ]
  },
  'Profit and Loss Report': {
    apiType: 'accounts-management',
    getRows: (rawData) => {
      const journals = rawData || []
      const accountBalances = {}

      // 1. Aggregate balances from journals
      journals.forEach((journal) => {
        if (!journal.items) return
        journal.items.forEach((item) => {
          const acc = item.accountId
          if (!acc || !acc._id) return
          if (!accountBalances[acc._id]) {
            accountBalances[acc._id] = {
              name: acc.name,
              category: acc.category,
              debit: 0,
              credit: 0
            }
          }
          accountBalances[acc._id].debit += Number(item.debit) || 0
          accountBalances[acc._id].credit += Number(item.credit) || 0
        })
      })

      const rows = []
      let totalIncome = 0
      let totalExpense = 0

      // I. REVENUE
      rows.push({
        category: 'I. REVENUE',
        particulars: 'Revenue from Operations',
        income: null,
        expense: null,
        _isSummary: true
      })
      Object.values(accountBalances)
        .filter((a) => ['Income', 'Other Income'].includes(a.category))
        .forEach((a) => {
          const bal = a.credit - a.debit
          if (bal !== 0) {
            totalIncome += bal
            rows.push({ category: '', particulars: `  - ${a.name}`, income: bal, expense: 0 })
          }
        })
      rows.push({
        category: '',
        particulars: 'TOTAL REVENUE (A)',
        income: totalIncome,
        expense: 0,
        _isSummary: true
      })

      rows.push({ category: '', particulars: '', income: null, expense: null })

      // II. EXPENSES
      rows.push({
        category: 'II. EXPENSES',
        particulars: 'Operating Expenses',
        income: null,
        expense: null,
        _isSummary: true
      })
      Object.values(accountBalances)
        .filter((a) => ['Expense', 'Cost Of Goods Sold', 'Other Expense'].includes(a.category))
        .forEach((a) => {
          const bal = a.debit - a.credit
          if (bal !== 0) {
            totalExpense += bal
            rows.push({ category: '', particulars: `  - ${a.name}`, income: 0, expense: bal })
          }
        })
      rows.push({
        category: '',
        particulars: 'TOTAL EXPENSES (B)',
        income: 0,
        expense: totalExpense,
        _isSummary: true
      })

      rows.push({ category: '', particulars: '', income: null, expense: null })

      // III. NET PROFIT
      const netProfit = totalIncome - totalExpense
      rows.push({
        category: 'III. NET PROFIT / (LOSS)',
        particulars: 'Profit / (Loss) for the Period (A - B)',
        type: netProfit >= 0 ? 'INCOME' : 'EXPENSE',
        amount: netProfit,
        isSummary: true
      })

      return rows
    },
    columns: [
      { text: 'Category', value: 'pnl_category' },
      { text: 'Particulars', value: 'pnl_particulars' },
      { text: 'Income (Cr)', value: 'pnl_income' },
      { text: 'Expense (Dr)', value: 'pnl_expense' }
    ]
  },
  'Balance Sheet Report': {
    apiType: 'accounts-management',
    getRows: (rawData) => {
      const journals = rawData || []
      const accountBalances = {} // id -> { name, category, debit, credit }

      // 1. Aggregate balances from journals
      journals.forEach((journal) => {
        if (!journal.items) return
        journal.items.forEach((item) => {
          const acc = item.accountId
          if (!acc || !acc._id) return
          if (!accountBalances[acc._id]) {
            accountBalances[acc._id] = {
              name: acc.name,
              category: acc.category,
              debit: 0,
              credit: 0
            }
          }
          accountBalances[acc._id].debit += Number(item.debit) || 0
          accountBalances[acc._id].credit += Number(item.credit) || 0
        })
      })

      // 2. Classify into Groups
      const groups = {
        EQUITY: [],
        LIABILITIES: {
          'Current Liabilities': [],
          'Non Current Liabilities': []
        },
        ASSETS: {
          'Current Assets': [],
          'Fixed Assets': [],
          'Other Assets': []
        }
      }

      let netProfit = 0

      Object.values(accountBalances).forEach((acc) => {
        const debit = acc.debit
        const credit = acc.credit
        const cat = acc.category
        const balance = Math.abs(debit - credit)

        if (balance === 0 && !['Income', 'Other Income', 'Expense', 'Cost Of Goods Sold', 'Other Expense'].includes(cat)) {
          return
        }

        if (['Income', 'Other Income'].includes(cat)) {
          netProfit += credit - debit
        } else if (['Expense', 'Cost Of Goods Sold', 'Other Expense'].includes(cat)) {
          netProfit -= debit - credit
        } else if (['Cash', 'Bank', 'Stock', 'Accounts Receivable', 'Other Current Asset'].includes(cat)) {
          groups.ASSETS['Current Assets'].push({ name: acc.name, category: cat, amount: (debit - credit) })
        } else if (cat === 'Fixed Asset') {
          groups.ASSETS['Fixed Assets'].push({ name: acc.name, category: cat, amount: (debit - credit) })
        } else if (['Long Term Asset', 'Other Asset'].includes(cat)) {
          groups.ASSETS['Other Assets'].push({ name: acc.name, category: cat, amount: (debit - credit) })
        } else if (['Accounts Payable', 'Other Current Liability', 'Other Liability'].includes(cat)) {
          groups.LIABILITIES['Current Liabilities'].push({ name: acc.name, category: cat, amount: (credit - debit) })
        } else if (cat === 'Long Term Liability') {
          groups.LIABILITIES['Non Current Liabilities'].push({ name: acc.name, category: cat, amount: (credit - debit) })
        } else if (cat === 'Equity') {
          groups.EQUITY.push({ name: acc.name, amount: (credit - debit) })
        }
      })
      groups.EQUITY.push({ name: 'Retained Earnings', amount: netProfit })

      const leList = []
      const assetsList = []

      // --- Left Side: Liabilities & Equities ---
      leList.push({ particulars: 'Liabilities', isMainHeader: true })
      let totalLiabilities = 0

      Object.keys(groups.LIABILITIES).forEach((groupName) => {
        const items = groups.LIABILITIES[groupName] || []
        let sectionTotal = 0
        if (items.length > 0) {
          leList.push({ particulars: groupName, isSubHeader: true })
          
          const categories = {}
          items.forEach(it => {
            if (!categories[it.category]) categories[it.category] = []
            categories[it.category].push(it)
          })

          Object.keys(categories).forEach(catName => {
            leList.push({ particulars: catName, isGroupHeader: true })
            let catTotal = 0
            categories[catName].forEach(it => {
               if (it.amount !== 0) {
                 leList.push({ particulars: it.name, amount: it.amount, isItem: true })
                 catTotal += it.amount
               }
            })
            leList.push({ particulars: `Total for ${catName}`, amount: catTotal, isCategoryTotal: true })
            sectionTotal += catTotal
          })
          leList.push({ particulars: `Total for ${groupName}`, amount: sectionTotal, isSectionTotal: true })
          totalLiabilities += sectionTotal
        } else {
          leList.push({ particulars: groupName, isSubHeader: true })
          leList.push({ particulars: `Total for ${groupName}`, amount: 0, isSectionTotal: true })
        }
      })
      leList.push({ particulars: `Total for Liabilities`, amount: totalLiabilities, isSectionTotal: true })

      let totalEquities = 0
      leList.push({ particulars: 'Equities', isSubHeader: true })
      groups.EQUITY.forEach(e => {
         if (e.amount !== 0) {
           leList.push({ particulars: e.name, amount: e.amount, isItem: true })
           totalEquities += e.amount
         }
      })
      leList.push({ particulars: `Total for Equities`, amount: totalEquities, isSectionTotal: true })

      const totalLeft = totalLiabilities + totalEquities

      // --- Right Side: Assets ---
      let totalAssets = 0

      Object.keys(groups.ASSETS).forEach((groupName) => {
        const items = groups.ASSETS[groupName] || []
        let sectionTotal = 0
        
        if (items.length > 0) {
          assetsList.push({ particulars: groupName, isSubHeader: true })
          
          const categories = {}
          items.forEach(it => {
            if (!categories[it.category]) categories[it.category] = []
            categories[it.category].push(it)
          })

          Object.keys(categories).forEach(catName => {
            assetsList.push({ particulars: catName, isGroupHeader: true })
            let catTotal = 0
            categories[catName].forEach(it => {
               if (it.amount !== 0) {
                 assetsList.push({ particulars: it.name, amount: it.amount, isItem: true })
                 catTotal += it.amount
               }
            })
            assetsList.push({ particulars: `Total for ${catName}`, amount: catTotal, isCategoryTotal: true })
            sectionTotal += catTotal
          })
          assetsList.push({ particulars: `Total for ${groupName}`, amount: sectionTotal, isSectionTotal: true })
          totalAssets += sectionTotal
        } else {
          assetsList.push({ particulars: groupName, isSubHeader: true })
          assetsList.push({ particulars: `Total for ${groupName}`, amount: 0, isSectionTotal: true })
        }
      })

      // 4. Merge into horizontal rows
      const rows = []
      const maxLen = Math.max(leList.length, assetsList.length)

      for (let i = 0; i < maxLen; i++) {
        const le = leList[i] || {}
        const as = assetsList[i] || {}

        // Format amount using Intl.NumberFormat for commas (e.g. 15,000.00)
        const formatAmt = (val) => {
          if (val === undefined || val === null) return ''
          return Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }

        rows.push({
          le_particulars: le.particulars || '',
          le_amount: formatAmt(le.amount),
          le_isMainHeader: !!le.isMainHeader,
          le_isGroupHeader: !!le.isGroupHeader,
          le_isSubGroupHeader: !!le.isSubGroupHeader,
          le_isItem: !!le.isItem,
          le_isCategoryTotal: !!le.isCategoryTotal,
          le_isSectionTotal: !!le.isSectionTotal,
          le_isMainTotal: !!le.isMainTotal,

          assets_particulars: as.particulars || '',
          assets_amount: formatAmt(as.amount),
          as_isMainHeader: !!as.isMainHeader,
          as_isSubHeader: !!as.isSubHeader,
          as_isGroupHeader: !!as.isGroupHeader,
          as_isSubGroupHeader: !!as.isSubGroupHeader,
          as_isItem: !!as.isItem,
          as_isCategoryTotal: !!as.isCategoryTotal,
          as_isSectionTotal: !!as.isSectionTotal,
          as_isMainTotal: !!as.isMainTotal
        })
      }

      // 5. Total Footer
      const formatAmtTotal = (val) => Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      rows.push({
        le_particulars: 'TOTAL LIABILITIES & EQUITIES',
        le_amount: formatAmtTotal(totalLeft),
        isGrandTotal: true,
        assets_particulars: 'TOTAL ASSETS',
        assets_amount: formatAmtTotal(totalAssets)
      })

      return rows
    },
    columns: [
      { text: 'Particulars (L&E)', value: 'le_particulars' },
      { text: 'Amount (Cr)', value: 'le_amount' },
      { text: 'Particulars (Assets)', value: 'assets_particulars' },
      { text: 'Amount (Dr)', value: 'assets_amount' }
    ]
  },
  'Vertical Balance Sheet Report': {
    apiType: 'accounts-management',
    getRows: (rawData) => {
      const journals = rawData || []
      const accountBalances = {}

      journals.forEach((journal) => {
        if (!journal.items) return
        journal.items.forEach((item) => {
          const acc = item.accountId
          if (!acc || !acc._id) return
          if (!accountBalances[acc._id]) {
            accountBalances[acc._id] = {
              name: acc.name,
              category: acc.category,
              debit: 0,
              credit: 0
            }
          }
          accountBalances[acc._id].debit += Number(item.debit) || 0
          accountBalances[acc._id].credit += Number(item.credit) || 0
        })
      })

      const groups = {
        EQUITY: [],
        LIABILITIES: {
          'Current Liabilities': [],
          'Non Current Liabilities': []
        },
        ASSETS: {
          'Current Assets': [],
          'Fixed Assets': [],
          'Other Assets': []
        }
      }

      let netProfit = 0

      Object.values(accountBalances).forEach((acc) => {
        const debit = acc.debit
        const credit = acc.credit
        const cat = acc.category
        const balance = Math.abs(debit - credit)

        if (balance === 0 && !['Income', 'Other Income', 'Expense', 'Cost Of Goods Sold', 'Other Expense'].includes(cat)) {
          return
        }

        if (['Income', 'Other Income'].includes(cat)) {
          netProfit += credit - debit
        } else if (['Expense', 'Cost Of Goods Sold', 'Other Expense'].includes(cat)) {
          netProfit -= debit - credit
        } else if (['Cash', 'Bank', 'Stock', 'Accounts Receivable', 'Other Current Asset'].includes(cat)) {
          groups.ASSETS['Current Assets'].push({ name: acc.name, category: cat, amount: (debit - credit) })
        } else if (cat === 'Fixed Asset') {
          groups.ASSETS['Fixed Assets'].push({ name: acc.name, category: cat, amount: (debit - credit) })
        } else if (['Long Term Asset', 'Other Asset'].includes(cat)) {
          groups.ASSETS['Other Assets'].push({ name: acc.name, category: cat, amount: (debit - credit) })
        } else if (['Accounts Payable', 'Other Current Liability', 'Other Liability'].includes(cat)) {
          groups.LIABILITIES['Current Liabilities'].push({ name: acc.name, category: cat, amount: (credit - debit) })
        } else if (cat === 'Long Term Liability') {
          groups.LIABILITIES['Non Current Liabilities'].push({ name: acc.name, category: cat, amount: (credit - debit) })
        } else if (cat === 'Equity') {
          groups.EQUITY.push({ name: acc.name, amount: (credit - debit) })
        }
      })
      groups.EQUITY.push({ name: 'Retained Earnings', amount: netProfit })

      const rows = []
      const formatAmt = (val) => {
        if (val === undefined || val === null) return ''
        return Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }

      // ---------------- ASSETS ----------------
      rows.push({ vbs_account: 'Assets', isMainHeader: true })
      let totalAssets = 0

      Object.keys(groups.ASSETS).forEach((groupName) => {
        const items = groups.ASSETS[groupName] || []
        let sectionTotal = 0
        
        if (items.length > 0) {
          rows.push({ vbs_account: groupName, isSubHeader: true })
          
          const categories = {}
          items.forEach(it => {
            if (!categories[it.category]) categories[it.category] = []
            categories[it.category].push(it)
          })

          Object.keys(categories).forEach(catName => {
            rows.push({ vbs_account: catName, isGroupHeader: true })
            let catTotal = 0
            categories[catName].forEach(it => {
               if (it.amount !== 0) {
                 rows.push({ vbs_account: it.name, vbs_code: '', vbs_total: formatAmt(it.amount), isItem: true })
                 catTotal += it.amount
               }
            })
            rows.push({ vbs_account: `Total for ${catName}`, vbs_total: formatAmt(catTotal), isCategoryTotal: true })
            sectionTotal += catTotal
          })
          rows.push({ vbs_account: `Total for ${groupName}`, vbs_total: formatAmt(sectionTotal), isSectionTotal: true })
          totalAssets += sectionTotal
        } else {
          rows.push({ vbs_account: groupName, isSubHeader: true })
          rows.push({ vbs_account: `Total for ${groupName}`, vbs_total: '0.00', isSectionTotal: true })
        }
      })
      
      rows.push({ vbs_account: 'Total for Assets', vbs_total: formatAmt(totalAssets), isMainTotal: true })
      
      // Spacer
      rows.push({ vbs_account: '', isSpacer: true })

      // ---------------- LIABILITIES & EQUITIES ----------------
      rows.push({ vbs_account: 'Liabilities & Equities', isMainHeader: true })
      rows.push({ vbs_account: 'Liabilities', isSubHeader: true })
      let totalLiabilities = 0

      Object.keys(groups.LIABILITIES).forEach((groupName) => {
        const items = groups.LIABILITIES[groupName] || []
        let sectionTotal = 0
        if (items.length > 0) {
          rows.push({ vbs_account: groupName, isGroupHeader: true })
          
          const categories = {}
          items.forEach(it => {
            if (!categories[it.category]) categories[it.category] = []
            categories[it.category].push(it)
          })

          Object.keys(categories).forEach(catName => {
            rows.push({ vbs_account: catName, isSubGroupHeader: true })
            let catTotal = 0
            categories[catName].forEach(it => {
               if (it.amount !== 0) {
                 rows.push({ vbs_account: it.name, vbs_code: '', vbs_total: formatAmt(it.amount), isItem: true })
                 catTotal += it.amount
               }
            })
            rows.push({ vbs_account: `Total for ${catName}`, vbs_total: formatAmt(catTotal), isCategoryTotal: true })
            sectionTotal += catTotal
          })
          rows.push({ vbs_account: `Total for ${groupName}`, vbs_total: formatAmt(sectionTotal), isSectionTotal: true })
          totalLiabilities += sectionTotal
        } else {
          rows.push({ vbs_account: groupName, isGroupHeader: true })
          rows.push({ vbs_account: `Total for ${groupName}`, vbs_total: '0.00', isSectionTotal: true })
        }
      })
      rows.push({ vbs_account: `Total for Liabilities`, vbs_total: formatAmt(totalLiabilities), isSectionTotal: true })

      let totalEquities = 0
      rows.push({ vbs_account: 'Equities', isGroupHeader: true })
      groups.EQUITY.forEach(e => {
         if (e.amount !== 0) {
           rows.push({ vbs_account: e.name, vbs_total: formatAmt(e.amount), isItem: true })
           totalEquities += e.amount
         }
      })
      rows.push({ vbs_account: `Total for Equities`, vbs_total: formatAmt(totalEquities), isSectionTotal: true })

      rows.push({ vbs_account: 'Total for Liabilities & Equities', vbs_total: formatAmt(totalLiabilities + totalEquities), isMainTotal: true })

      return rows
    },
    columns: [
      { text: 'ACCOUNT', value: 'vbs_account' },
      { text: 'ACCOUNT CODE', value: 'vbs_code' },
      { text: 'TOTAL', value: 'vbs_total' }
    ]
  }
}
